import type { SuggestionTier } from './rankLocalSpecialists';

/**
 * Per-viewer memory of what this browser was shown and ignored, for tiered profiles (config.tiers).
 * Purpose: a patient who keeps coming back and never takes the suggested kind of doctor (say, online)
 * should be offered the next kind (office) instead of the same card forever.
 *
 * Stored only in this browser's localStorage, keyed by the profile slug: doctor slugs and counters —
 * no personal data, nothing sent anywhere. Expires after MAX_AGE_MS; every read/write is guarded
 * (private mode, blocked storage, quota) and a broken value is treated as empty.
 *
 * A "view" counts as ignored until a click on that doctor/tier resets it.
 */
export const IGNORED_VIEWS_BEFORE_ROTATION = 2;
const MAX_AGE_MS = 30 * 24 * 3600 * 1000;
const STORAGE_PREFIX = 'p24:local-specialists:';
const TIER_ORDER: SuggestionTier[] = ['clinic', 'online', 'office'];

export interface ViewerMemory {
  /** Views without a click, per doctor slug. */
  doctors: Record<string, number>;
  /** Views without a click, per tier. */
  tiers: Partial<Record<SuggestionTier, number>>;
  updatedAt: number;
}

const empty = (): ViewerMemory => ({ doctors: {}, tiers: {}, updatedAt: Date.now() });

const isCounterMap = (value: unknown): value is Record<string, number> =>
  !!value && typeof value === 'object' && Object.values(value as object).every(item => Number.isFinite(item));

const storage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

export const readViewerMemory = (ownSlug: string, now = Date.now()): ViewerMemory => {
  try {
    const raw = storage()?.getItem(STORAGE_PREFIX + ownSlug);
    if (!raw) return empty();
    const parsed = JSON.parse(raw);
    if (!isCounterMap(parsed?.doctors) || !isCounterMap(parsed?.tiers) || !Number.isFinite(parsed?.updatedAt)) return empty();
    if (now - parsed.updatedAt > MAX_AGE_MS) return empty();
    return parsed as ViewerMemory;
  } catch {
    return empty();
  }
};

const write = (ownSlug: string, memory: ViewerMemory) => {
  try {
    storage()?.setItem(STORAGE_PREFIX + ownSlug, JSON.stringify({ ...memory, updatedAt: Date.now() }));
  } catch {
    // Storage unavailable or full: the card simply does not adapt for this viewer.
  }
};

/** The shown doctors were viewed (not yet clicked). */
export const recordViewerViews = (ownSlug: string, shown: { slug: string; tier?: SuggestionTier | null }[]) => {
  if (!shown.length) return;
  const memory = readViewerMemory(ownSlug);
  const tiersSeen = new Set<SuggestionTier>();
  shown.forEach(({ slug, tier }) => {
    if (slug) memory.doctors[slug] = (memory.doctors[slug] ?? 0) + 1;
    if (tier) tiersSeen.add(tier);
  });
  tiersSeen.forEach(tier => {
    memory.tiers[tier] = (memory.tiers[tier] ?? 0) + 1;
  });
  write(ownSlug, memory);
};

/** The viewer chose this doctor: neither the doctor nor that tier counts as ignored any more. */
export const recordViewerClick = (ownSlug: string, slug: string, tier?: SuggestionTier | null) => {
  const memory = readViewerMemory(ownSlug);
  delete memory.doctors[slug];
  if (tier) delete memory.tiers[tier];
  write(ownSlug, memory);
};

const ignored = (count?: number) => (count ?? 0) >= IGNORED_VIEWS_BEFORE_ROTATION;

/**
 * Profile card (one doctor): the first tier, in clinic → online → office order, that this viewer has
 * not ignored IGNORED_VIEWS_BEFORE_ROTATION times; inside it, the first doctor not ignored as often.
 * When everything was ignored, the least-viewed doctor. Returns the index into `doctors` and how many
 * available tiers were skipped for this viewer (for analytics).
 */
export const pickForViewer = (
  doctors: { _tier?: SuggestionTier }[],
  slugs: string[],
  memory: ViewerMemory,
): { index: number; tierSkips: number } => {
  if (!doctors.length) return { index: -1, tierSkips: 0 };
  const available = TIER_ORDER.filter(tier => doctors.some(doctor => doctor._tier === tier));
  let tierSkips = 0;
  for (const tier of available) {
    if (ignored(memory.tiers[tier])) {
      tierSkips += 1;
      continue;
    }
    const inTier = doctors.map((doctor, index) => ({ doctor, index })).filter(item => item.doctor._tier === tier);
    const fresh = inTier.find(item => !ignored(memory.doctors[slugs[item.index]]));
    return { index: (fresh ?? inTier[0]).index, tierSkips };
  }
  let best = 0;
  doctors.forEach((_, index) => {
    if ((memory.doctors[slugs[index]] ?? 0) < (memory.doctors[slugs[best]] ?? 0)) best = index;
  });
  return { index: best, tierSkips };
};

/** Carousel: doctors this viewer ignored IGNORED_VIEWS_BEFORE_ROTATION times move to the end (stable). */
export const demoteIgnoredForViewer = <T>(doctors: T[], slugs: string[], memory: ViewerMemory): { list: T[]; demoted: number } => {
  const keep: T[] = [];
  const later: T[] = [];
  doctors.forEach((doctor, index) => (ignored(memory.doctors[slugs[index]]) ? later : keep).push(doctor));
  return { list: [...keep, ...later], demoted: later.length };
};
